import {createFragmentContainer, graphql} from 'react-relay';
import {cloneDeep} from 'lodash';
import {ESTIMATION_UNIT} from '../../../../constants';
import util from '../../../shared/util/util';

const setProjectGroupData = (viewer, projectGroup, calculatedDataMap) => {
	const isHourEstimated = projectGroup.node.projects?.edges?.length
		? projectGroup.node.projects.edges[0].node.estimationUnit === ESTIMATION_UNIT.HOURS
		: null;
	let approvedScope = 0,
		remaining = 0,
		reported = 0,
		startDate = null,
		endDate = null,
		tasksCount = 0,
		doneTasksCount = 0,
		projectPersonsCount = null,
		factor = null;
	projectGroup.isCalculatedDataReady = true;
	projectGroup.node.projects?.edges?.forEach(project => {
		project.node.showProject = true;
		if (isHourEstimated === false && factor === null) {
			factor = project.node.minutesPerEstimationPoint;
		}

		const calculatedData = calculatedDataMap ? calculatedDataMap.get(project.node.id) : null;
		if (!calculatedData) {
			projectGroup.isCalculatedDataReady = false;
		}
		/*
		 * ESTIMATION, TIME RELATED FIELDS
		 */
		approvedScope += calculatedData ? calculatedData.forecast : 0;
		remaining += calculatedData ? calculatedData.remaining : 0;
		reported += calculatedData ? calculatedData.reported : 0;
		/*
		 * DATES
		 */
		if (project.node.projectStartYear) {
			const projectStartDate = util.CreateNonUtcMomentDate(
				project.node.projectStartYear,
				project.node.projectStartMonth,
				project.node.projectStartDay
			);
			if (startDate === null || (startDate !== null && startDate.isAfter(projectStartDate))) {
				startDate = projectStartDate;
			}
		}
		if (project.node.projectEndYear) {
			const projectEndDate = util.CreateNonUtcMomentDate(
				project.node.projectEndYear,
				project.node.projectEndMonth,
				project.node.projectEndDay
			);
			if (endDate === null || (endDate !== null && endDate.isBefore(projectEndDate))) {
				endDate = projectEndDate;
			}
		}
		/*
		 * COUNTS
		 */
		tasksCount += calculatedData ? calculatedData.tasksCount : 0;
		doneTasksCount += calculatedData ? calculatedData.doneTasksCount : 0;
		if (projectPersonsCount === null) {
			projectPersonsCount = calculatedData ? calculatedData.projectPersonsCount : 0;
		}
	});
	projectGroup.node.isHourEstimated = isHourEstimated;
	projectGroup.node.minutesPerEstimationPoint = factor;
	projectGroup.node.completion = util.calculateElementProgress(
		approvedScope,
		reported,
		remaining,
		tasksCount,
		doneTasksCount,
		isHourEstimated,
		factor
	);
	projectGroup.node.forecast = approvedScope;
	projectGroup.node.reported = reported;
	projectGroup.node.remaining = remaining;
	projectGroup.node.startDate = startDate;
	projectGroup.node.endDate = endDate;
	projectGroup.node.tasksCount = tasksCount;
	projectGroup.node.doneTasksCount = doneTasksCount;
	projectGroup.node.projectPersonsCount = projectPersonsCount;

	return projectGroup;
};

const ProjectsLoader = ({
	viewer,
	children,
	calculatedDataMap,
	projectFromOutside,
	projectGroupsFromOutside,
	project_status,
}) => {
	let projectsAndGroups = [];
	if (projectGroupsFromOutside) {
		projectGroupsFromOutside.edges.forEach(pGroup => {
			projectsAndGroups.push(setProjectGroupData(viewer, cloneDeep(pGroup), calculatedDataMap));
		});
	}

	// The second clause here is to avoid an error which occurs if you disable the feature flag while the page is active. When the feature flag is cleaned up, it should similarly be removed.
	if (projectFromOutside) {
		projectFromOutside.edges
			.filter(p => p.node.status === project_status)
			.filter(p => viewer.client || !p.node.isInProjectGroup)
			.forEach(p => projectsAndGroups.push(p));
	}

	return children({data: projectsAndGroups});
};

export const projectsLoaderQuery = graphql`
	query ProjectsLoader_Query {
		viewer {
			actualPersonId
			component(name: "projects_loader")
			...ProjectsLoader_viewer
		}
	}
`;

graphql`
	fragment ProjectsLoader_financialsMultiCurrencies on ProjectType {
		financialNumbers {
			baselineRevenue
			billablePlannedTimeAndExpenses
			actualRevenue
			remainingRevenue
			forecastRevenue
			recognitionOpenRevenue
			recognitionForecastRevenue
			recognitionLockedRevenue
			plannedCost
			actualCost
			forecastCost
			remainingCost
			plannedProfit
			forecastProfit
			invoiced
			paid
		}
		financialNumbersProjectCurrency {
			baselineRevenue
			billablePlannedTimeAndExpenses
			actualRevenue
			remainingRevenue
			forecastRevenue
			recognitionOpenRevenue
			recognitionForecastRevenue
			recognitionLockedRevenue
			plannedCost
			actualCost
			forecastCost
			remainingCost
			plannedProfit
			forecastProfit
			invoiced
			paid
		}
	}
`;

graphql`
	fragment ProjectsLoader_financialsCompanyCurrency on ProjectType {
		financialNumbers {
			baselineRevenue
			billablePlannedTimeAndExpenses
			actualRevenue
			remainingRevenue
			forecastRevenue
			recognitionOpenRevenue
			recognitionForecastRevenue
			recognitionLockedRevenue
			plannedCost
			actualCost
			forecastCost
			remainingCost
			plannedProfit
			forecastProfit
			invoiced
			paid
		}
	}
`;

graphql`
	fragment ProjectsLoader_project on ProjectType {
		id
		name
		budgetType
		status
		description
		statusColor
		companyProjectId
		readOnlyAccess
		budget
		budgetBaseCurrency
		projectColor
		estimationUnit
		minutesPerEstimationPoint
		projectStartDay
		projectStartMonth
		projectStartYear
		projectEndDay
		projectEndMonth
		projectEndYear
		baselineWinChance
		sprintTimeBox
		currentProjectStatus {
			id
			color
			person {
				id
				firstName
				lastName
			}
		}
		client {
			id
			name
			logoId
			logoDefaultId
		}
		rateCard {
			id
			name
			currency
			parentRateCardId
		}
		progress
		manualProgressOnProjectEnabled
		manualProgressOnPhasesEnabled
		manualProgressOnTasksEnabled
		priorityLevel {
			id
			name
			weight
		}
		projectLabels(first: 10000) {
			edges {
				node {
					id
					label {
						id
						name
						color
					}
				}
			}
		}
		projectPersons(first: 1000000, contactsOnly: true) {
			edges {
				node {
					id
					isContactPerson
					person {
						id
						firstName
						lastName
						profilePictureId
						profilePictureDefaultId
						initials
					}
				}
			}
		}
	}
`;

// This fragment container barely does anything, but because of the funky LoadMore approach taken in overview_projects it still needs to be a fragment container.
export default createFragmentContainer(ProjectsLoader, {
	viewer: graphql`
		fragment ProjectsLoader_viewer on Viewer {
			availableFeatureFlags {
				key
			}
			client {
				id
			}
			company {
				xeroEnabled
			}
		}
	`,
});
